import React, { useEffect, useRef, useState } from 'react';
import useInsightSuggest from '../hooks/useInsightSuggest';

const SearchIcon = () => (
    <svg
        className="pointer-events-none absolute left-[8px] top-1/2 -translate-y-1/2"
        width="20" height="20" viewBox="0 0 20 20" fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path d="M8.75 15C12.2018 15 15 12.2018 15 8.75C15 5.29822 12.2018 2.5 8.75 2.5C5.29822 2.5 2.5 5.29822 2.5 8.75C2.5 12.2018 5.29822 15 8.75 15Z" stroke="#636369" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M13.1699 13.1699L17.5004 17.5004" stroke="#636369" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export default function FeedSearchInput({
    value,
    onSubmit,
    onSelectSymbol,
    onSelectInsight,
    placeholder = 'Tìm kiếm cổ phiếu...',
    onFocus,
    onBlur,
}) {
    const [draft, setDraft] = useState(value || '');
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const containerRef = useRef(null);

    useEffect(() => {
        setDraft(value || '');
    }, [value]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setActiveIndex(-1);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const { suggestions, loading } = useInsightSuggest(draft, { enabled: open });

    const showDropdown = open && draft.trim().length >= 1 && (loading || suggestions.length > 0);

    const symbolItems = suggestions.filter((s) => s.type === 'symbol');
    const insightItems = suggestions.filter((s) => s.type === 'insight');

    const handleChange = (e) => {
        const next = e.target.value;
        setDraft(next);
        setOpen(true);
        setActiveIndex(-1);
        if (next === '' && (value || '') !== '') {
            onSubmit?.('');
        }
    };

    const commitText = () => {
        setOpen(false);
        setActiveIndex(-1);
        onSubmit?.(draft.trim());
    };

    const pickSuggestion = (s) => {
        setOpen(false);
        setActiveIndex(-1);
        if (s.type === 'symbol') {
            setDraft('');
            onSelectSymbol?.(s);
        } else if (s.type === 'insight') {
            onSelectInsight?.(s);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && suggestions[activeIndex]) {
                pickSuggestion(suggestions[activeIndex]);
            } else {
                commitText();
            }
            return;
        }
        if (e.key === 'Escape') {
            setOpen(false);
            setActiveIndex(-1);
            return;
        }
        if (e.key === 'ArrowDown' && suggestions.length) {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % suggestions.length);
            return;
        }
        if (e.key === 'ArrowUp' && suggestions.length) {
            e.preventDefault();
            setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        }
    };

    return (
        <div ref={containerRef} className="w-full relative h-[32px] border border-[#ECECED] rounded-[8px] bg-[#F5F5F5]">
            <SearchIcon />
            <input
                type="search"
                value={draft}
                onChange={handleChange}
                onFocus={(e) => {
                    setOpen(true);
                    onFocus?.(e);
                }}
                onBlur={(e) => {
                    onBlur?.(e);
                }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="text-[14px] text-[#111827] placeholder:text-[#A0A0A5] w-full h-full pl-[36px] pr-[14px] appearance-none rounded-[8px] bg-[#f5f5f5] border border-[#ececed] focus:outline-none focus:border-[#d1d5db] transition-colors"
            />
            {showDropdown && (
                <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 max-h-[360px] overflow-y-auto rounded-[16px] border border-[#EEF0F3] bg-white shadow-[0_4px_16px_rgba(15,23,42,0.06),0_2px_6px_rgba(15,23,42,0.04)]">
                    {loading && suggestions.length === 0 && (
                        <div className="px-[14px] py-[10px] text-[12px] text-[#9CA3AF]">Đang tìm…</div>
                    )}
                    {!loading && suggestions.length === 0 && (
                        <div className="px-[14px] py-[10px] text-[12px] text-[#9CA3AF]">Không có gợi ý</div>
                    )}
                    {suggestions.length > 0 && (
                        <div>
                            {symbolItems.length > 0 && (
                                <div className="px-[12px] pt-[14px] pb-[8px] flex flex-wrap gap-[6px]">
                                    {symbolItems.map((s, idx) => {
                                        const active = idx === activeIndex;
                                        return (
                                            <SymbolPill
                                                key={`symbol-${s.symbol}-${idx}`}
                                                item={s}
                                                active={active}
                                                onMouseEnter={() => setActiveIndex(idx)}
                                                onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                            {insightItems.length > 0 && (
                                <ul className="py-[4px]">
                                    {insightItems.map((s, idx) => {
                                        const flatIndex = symbolItems.length + idx;
                                        const active = flatIndex === activeIndex;
                                        return (
                                            <li
                                                key={`insight-${s.id}-${idx}`}
                                                role="option"
                                                aria-selected={active}
                                                onMouseEnter={() => setActiveIndex(flatIndex)}
                                                onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                                                className={`px-[14px] py-[8px] cursor-pointer ${active ? 'bg-[#F1F5FF]' : 'hover:bg-[#F8FAFC]'}`}
                                            >
                                                <InsightRow item={s} />
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


function SymbolPill({ item, active, onMouseEnter, onMouseDown }) {
    return (
        <button
            type="button"
            onMouseEnter={onMouseEnter}
            onMouseDown={onMouseDown}
            className={`inline-flex items-center h-[26px] px-[10px] rounded-full border text-[12px] font-medium transition-colors ${
                active
                    ? 'bg-[#EEF2FF] border-[#C7D2FE] text-[#4338CA]'
                    : 'bg-[#F8FAFC] border-[#E2E8F0] text-[#0F172A] hover:bg-[#EEF2FF] hover:border-[#C7D2FE]'
            }`}
        >
            {item.symbol}
        </button>
    );
}


function InsightRow({ item }) {
    const timeLabel = formatTime(item.snapshot_at);
    return (
        <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium text-[#0F172A] line-clamp-1">
                {item.title || item.id}
            </div>
            {item.summary && (
                <div className="text-[12px] text-[#64748B] line-clamp-2 mt-[2px]">
                    {item.summary}
                </div>
            )}
            <div className="flex items-center gap-[6px] mt-[4px] text-[11px] text-[#94A3B8]">
                {item.symbol && (
                    <span className="inline-flex items-center px-[6px] py-[1px] rounded bg-[#F1F5F9] text-[#475569] font-medium">
                        {item.symbol}
                    </span>
                )}
                {timeLabel && <span>{timeLabel}</span>}
            </div>
        </div>
    );
}


function formatTime(iso) {
    if (!iso) return null;
    try {
        const date = new Date(iso);
        const diffMs = Date.now() - date.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1) return 'vừa xong';
        if (diffMin < 60) return `${diffMin} phút trước`;
        const diffHour = Math.floor(diffMin / 60);
        if (diffHour < 24) return `${diffHour} giờ trước`;
        const diffDay = Math.floor(diffHour / 24);
        if (diffDay < 7) return `${diffDay} ngày trước`;
        return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
        return null;
    }
}
